import React, { useRef, useState } from "react";
import axios from "axios";
import {
  Breadcrumbs,
  Button,
  Card,
  Database01,
  PageHeader,
  Select,
  TextInput,
  TileGroup,
  Upload01,
} from "@opendate-app/setlist";

import DataDrivenSegmentForm from "./DataDrivenSegmentForm";
import { ensureConditionsComplete } from "./FanFilters/conditionGate";
import { useSegmentNewContext } from "./SegmentNewContainer";
import { toastError } from "../../../shared/toastHelper";

const styles = {
  page: { display: "flex", flexDirection: "column", gap: 24, padding: "0 0 48px" },
  settings: { display: "flex", flexDirection: "column", gap: 20 },
  fields: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 },
  sourceLabel: { display: "block", marginBottom: 8, fontSize: 14, lineHeight: "20px", color: "var(--sl-text-secondary, #414651)" },
  manual: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    padding: "40px 24px",
    textAlign: "center",
    border: "1px dashed var(--sl-border-primary, #D5D7DA)",
    borderRadius: 12,
    fontSize: 14,
    lineHeight: "20px",
    color: "var(--sl-text-tertiary, #535862)",
  },
};

const SOURCE_TILES = [
  { value: "Segments::DataDriven", label: "Data driven", supportingText: "Build from attributes about events or contacts.", icon: Database01 },
  { value: "Segments::Manual", label: "Manual", supportingText: "Upload a CSV of contacts.", icon: Upload01 },
];

const SegmentNew = () => {
  const {
    csrfToken,
    team,
    venueOwnerships,
    selectedVenueOwnership,
    setSelectedVenueOwnership,
    segment,
    setSegment,
    aiSegmentBuilder,
  } = useSegmentNewContext();
  const [fanFilterGroupId, setFanFilterGroupId] = useState(segment.fan_filter_group?.id || 0);
  const submittingRef = useRef(false);
  const [createdSegmentId, setCreatedSegmentId] = useState(null);
  const failSubmit = (error, fallback) => {
    submittingRef.current = false;
    toastError((error.response?.data || []).join?.(", ") || fallback);
  };

  const ensureSegment = () => {
    if (createdSegmentId) {
      setSegment((current) => ({ ...current, id: createdSegmentId }));
      return Promise.resolve(createdSegmentId);
    }
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios.post(`/teams/${team.id}/segments`, { segment: { ...segment, name: segment.name.trim() || "Untitled segment" } })
      .then(({ data }) => {
        setSegment((current) => ({ ...current, id: data.id }));
        return data.id;
      });
  };

  const saveBuiltSegment = () => {
    axios.put(`/teams/${team.id}/segments/${segment.id}`, {
      segment: { name: segment.name, venue_ownership_id: segment.venue_ownership_id },
    })
      .then(() => {
        window.location.href = `/teams/${team.id}/segments/${segment.id}`;
      })
      .catch((error) => failSubmit(error, "Couldn't save the segment."));
  };

  const createSegment = () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    if (segment.type !== "Segments::DataDriven") return postSegment();

    ensureConditionsComplete(team.id, fanFilterGroupId)
      .then((complete) => {
        if (!complete) {
          submittingRef.current = false;
          return;
        }
        segment.id ? saveBuiltSegment() : postSegment();
      })
      .catch((error) => failSubmit(error, "Couldn't check the segment's conditions."));
  };

  // A retry after a failed attach updates the segment the first attempt created.
  const postSegment = () => {
    const created = createdSegmentId
      ? axios.put(`/teams/${team.id}/segments/${createdSegmentId}`, {
        segment: { name: segment.name, venue_ownership_id: segment.venue_ownership_id },
      }).then(() => createdSegmentId)
      : axios.post(`/teams/${team.id}/segments`, { segment: segment })
        .then(({ data }) => {
          setCreatedSegmentId(data.id);
          return data.id;
        });

    created
      .then((id) => {
        if (segment.type === "Segments::Manual") {
          window.location.href = `/teams/${team.id}/segments/${id}`;
        } else if (!fanFilterGroupId) {
          window.location.href = `/teams/${team.id}/segments/${id}/edit`;
        } else {
          return axios.put(`/teams/${team.id}/fan_filter_groups/${fanFilterGroupId}`, {
            fan_filter_group: { segment_id: id }
          })
            .then(() => {
              window.location.href = `/teams/${team.id}/segments/${id}`;
            });
        }
      })
      .catch((error) => failSubmit(error, "Couldn't save the segment."));
  };

  const segmentsPath = `/teams/${team.id}/segments`;

  return (
    <div style={styles.page}>
      <PageHeader
        divider={false}
        title="New segment"
        breadcrumbs={<Breadcrumbs items={[{ label: "Segments", href: segmentsPath }, { label: "New segment" }]} />}
        actions={(
          <>
            <Button variant="secondary" onClick={() => { window.location.href = segmentsPath; }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={createSegment}>
              Create segment
            </Button>
          </>
        )}
      />
      <Card>
        <form style={styles.settings} onSubmit={(e) => e.preventDefault()}>
          <div style={styles.fields}>
            <TextInput
              label="Segment name"
              id="name"
              placeholder="e.g. Likely buyers for the next event"
              value={segment.name || ""}
              onChange={(e) => setSegment({...segment, name: e.target.value})}
            />
            <Select
              label="Workspace"
              id="venueOwnership"
              options={(venueOwnerships || []).map((venueOwnership) => ({
                value: venueOwnership.id,
                label: venueOwnership.venueable?.name || venueOwnership.name,
              }))}
              value={selectedVenueOwnership?.id || null}
              onChange={(value) => {
                const venueOwnership = venueOwnerships.find(vo => vo.id === value);
                setSelectedVenueOwnership(venueOwnership);
                setSegment({...segment, venue_ownership_id: venueOwnership?.id});
              }}
            />
          </div>
          <div>
            <span id="audience-source-label" style={styles.sourceLabel}>Audience source</span>
            <TileGroup
              aria-labelledby="audience-source-label"
              name="audienceSource"
              columns={2}
              items={SOURCE_TILES.map((tile) => ({ ...tile, disabled: !!(segment.id || createdSegmentId) && tile.value !== segment.type }))}
              value={segment.type}
              onChange={(type) => setSegment({...segment, type})}
            />
          </div>
        </form>
      </Card>
      {segment.type === "Segments::DataDriven" ? (
        <DataDrivenSegmentForm
          csrfToken={csrfToken}
          team={team}
          segment={segment}
          setSegment={setSegment}
          fanFilterGroupId={fanFilterGroupId}
          setFanFilterGroupId={setFanFilterGroupId}
          aiSegmentBuilder={aiSegmentBuilder}
          ensureSegment={ensureSegment}
          showAudienceEstimate
        />
      ) : (
        <Card>
          <div style={styles.manual}>
            <Upload01 width={24} height={24} aria-hidden="true" />
            <div>Create the segment, then import your contacts from a CSV on the segment's page.</div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default SegmentNew;
